import { useNavigate, useLocation } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import styles from "./Header.module.css";
import { useCallback, useEffect, useState, type MouseEvent } from "react";
import andyHdLogo from "@/assets/andyhdlogo.png";
import { tokenService } from "@/services/auth/token.service";

const SECTION_IDS = ["home", "about", "how-it-works", "pricing", "faq"] as const;

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<(typeof SECTION_IDS)[number]>("home");
  const navigate = useNavigate();
  const location = useLocation();
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSectionNavigation = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      event.preventDefault();
      closeMobileMenu();
      setActiveSection(sectionId as (typeof SECTION_IDS)[number]);

      if (sectionId === "home") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.history.replaceState(null, "", "#home");
        return;
      }

      const target = document.getElementById(sectionId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `#${sectionId}`);
      }
    },
    []
  );

  const handleAvatarClick = useCallback(() => {
    closeMobileMenu();

    // if (!tokenService.hasToken()) {
    //   navigate(routes.login);
    //   return;
    // }

    if (!tokenService.hasToken()) {
      navigate(routes.login, {
        state: {
          from: `${location.pathname}${location.search}${location.hash}`,
        },
      });
      return;
    }
    navigate(routes.homepage);
    return;
  }, [navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    const initialHash = window.location.hash.replace("#", "");
    if (SECTION_IDS.includes(initialHash as (typeof SECTION_IDS)[number])) {
      setActiveSection(initialHash as (typeof SECTION_IDS)[number]);
    }

    const sections = SECTION_IDS.map((id) => document.getElementById(id)).filter(
      (section): section is HTMLElement => Boolean(section)
    );
    if (!sections.length) return;
    const firstSection = sections[0];
    if (!firstSection) return;

    const updateActiveSection = () => {
      const viewportAnchor = window.scrollY + 140;

      let currentSection = firstSection.id as (typeof SECTION_IDS)[number];
      for (const section of sections) {
        if (section.offsetTop <= viewportAnchor) {
          currentSection = section.id as (typeof SECTION_IDS)[number];
        } else {
          break;
        }
      }

      setActiveSection((prev) => (prev === currentSection ? prev : currentSection));
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, []);

  return (
    <>
      {isMobileMenuOpen && (
        <div className={styles.mobileMenuOverlay} onClick={closeMobileMenu} aria-hidden="true" />
      )}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          {/* Logo Section */}
          <div
            className={styles.logoSection}
            // onClick={() => navigate(tokenService.hasToken() ? routes.homepage : routes.login)}
            onClick={() =>
              navigate(
                tokenService.hasToken() ? routes.homepage : routes.login,
                !tokenService.hasToken()
                  ? {
                      state: {
                        from: `${location.pathname}${location.search}${location.hash}`,
                      },
                    }
                  : undefined
              )
            }
          >
            <div className={styles.logoIcon}>
              <img src={andyHdLogo} alt="Andy logo" className={styles.andyLogo} />
            </div>
          </div>

          {/* Hamburger Menu Button */}
          <button
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <span
              className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`}
            ></span>
            <span
              className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`}
            ></span>
            <span
              className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`}
            ></span>
          </button>

          {/* Navigation Links */}
          <nav className={`${styles.nav} ${isMobileMenuOpen ? styles.navOpen : ""}`}>
            <a
              href="#home"
              className={activeSection === "home" ? styles.navLinkActive : styles.navLink}
              onClick={(event) => handleSectionNavigation(event, "home")}
            >
              Home
            </a>
            <a
              href="#about"
              className={activeSection === "about" ? styles.navLinkActive : styles.navLink}
              onClick={(event) => handleSectionNavigation(event, "about")}
            >
              About
            </a>
            <a
              href="#how-it-works"
              className={activeSection === "how-it-works" ? styles.navLinkActive : styles.navLink}
              onClick={(event) => handleSectionNavigation(event, "how-it-works")}
            >
              How It Works
            </a>
            <a
              href="#pricing"
              className={activeSection === "pricing" ? styles.navLinkActive : styles.navLink}
              onClick={(event) => handleSectionNavigation(event, "pricing")}
            >
              Pricing
            </a>
            <a
              href="#faq"
              className={activeSection === "faq" ? styles.navLinkActive : styles.navLink}
              onClick={(event) => handleSectionNavigation(event, "faq")}
            >
              FAQ&apos;s
            </a>
            <div
              className={styles.avatarSection}
              onClick={handleAvatarClick}
              style={{ cursor: "pointer" }}
            >
              <div className={styles.avatar}>
                <img src={andyHdLogo} alt="Andy logo" className={styles.avatarLogo} />
              </div>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
};
